/**
 * Self-Referral Landing Page Component
 *
 * Main orchestrator component for the self-referral workflow.
 * Manages the complete user journey from form submission through
 * dietitian selection to referral completion.
 *
 * Features:
 * - URL parameter extraction for provider ID
 * - Multi-step workflow orchestration
 * - Session management and expiration handling
 * - Error boundaries and loading states
 * - Navigation between workflow steps
 * - Storage operations and cleanup
 * - SEO and accessibility optimizations
 *
 * @module components/self-referral/SelfReferralLandingPage
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { cn } from '@/utils/cn';
import { Layout } from '@/components/layout/Layout';
import { SelfReferralWizard } from './SelfReferralWizard';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import { BodyText, TitleText } from '../ui';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useSelfReferralWorkflow } from '@/hooks/useSelfReferralWorkflow';
import { getLogger } from '@/services/shared/LoggerFactory';
import type {
  SelfReferralFormData,
  MatchedDietitian,
  WorkflowStep
} from './types';

/**
 * Error Boundary Component for Self-Referral Workflow
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class SelfReferralErrorBoundaryCore extends React.Component<
  React.PropsWithChildren<{ onReset: () => void; isDark: boolean }>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<{ onReset: () => void; isDark: boolean }>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Self-referral workflow error:', error, errorInfo);
  }

  render() {
    const { isDark } = this.props;

    if (this.state.hasError) {
      return (
        <div className={cn(
          'min-h-screen flex items-center justify-center px-4',
          isDark ? 'bg-secondary-900' : 'bg-secondary-50'
        )}>
          <div className={cn(
            'max-w-md w-full rounded-lg shadow-md p-6 text-center',
            isDark ? 'bg-secondary-800' : 'bg-white'
          )}>
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-error-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className={cn(
              'text-lg font-semibold mb-2',
              isDark ? 'text-secondary-100' : 'text-secondary-900'
            )}>
              Something went wrong
            </h2>
            <p className={cn(
              'mb-4',
              isDark ? 'text-secondary-400' : 'text-secondary-600'
            )}>
              We encountered an unexpected error. Please try starting over.
            </p>
            <button
              onClick={this.props.onReset}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Functional wrapper for ErrorBoundary that uses useTheme hook
 */
const SelfReferralErrorBoundary: React.FC<React.PropsWithChildren<{ onReset: () => void }>> = ({ children, onReset }) => {
  const { isDark } = useTheme();

  return (
    <SelfReferralErrorBoundaryCore onReset={onReset} isDark={isDark}>
      {children}
    </SelfReferralErrorBoundaryCore>
  );
};

/**
 * Step-specific content for SEO and accessibility
 */
const STEP_CONTENT: Record<WorkflowStep, { title: string; description: string }> = {
  form: {
    title: 'Great! Lets connect you with a dietitian!',
    description: 'Complete your information to get matched with the perfect dietitian for your needs'
  },
  selection: {
    title: 'Your top dietitian matches, pick one to begin!',
    description: 'Select from our recommended dietitians based on your needs'
  },
  completed: {
    title: 'Referral Complete',
    description: 'Your referral has been created successfully'
  },
  confirmation: {
    title: 'Referral Complete',
    description: 'Your referral has been created successfully'
  }
};

/**
 * Selection-step content when the session returned no dietitians
 * (design-dietitian-accreditation §4.8): a hand-off, not "pick one".
 */
const EMPTY_SELECTION_CONTENT = {
  title: 'Your details have been received',
  description: "Here's what happens next"
};

/**
 * Main Self-Referral Landing Page Component
 */
export const SelfReferralLandingPage: React.FC = () => {
  const logger = getLogger();
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [isInitializing, setIsInitializing] = useState(true);

  // Validate provider ID
  useEffect(() => {
    if (!providerId) {
      logger.error('No provider ID found in URL parameters');
      navigate('/404', { replace: true });
      return;
    }
    setIsInitializing(false);
  }, []);

  // Initialize workflow hook
  const {
    workflowState,
    matches,
    isLoading,
    error,
    submitForm,
    selectDietitian,
    sessionToken,
    resetWorkflow,
    handleSessionExpire
  } = useSelfReferralWorkflow(providerId || '');

  /**
   * Handle form submission
   */
  const handleFormSubmit = useCallback(async (formData: SelfReferralFormData) => {
    try {
      await submitForm(formData);
    } catch (error) {
      logger.error('Form submission failed:', error);
    }
  }, [submitForm]);

  /**
   * Handle dietitian selection
   */
  const handleDietitianSelect = useCallback(async (dietitian: MatchedDietitian) => {
    try {
      await selectDietitian(dietitian);
    } catch (error) {
      logger.error('Dietitian selection failed:', error);
    }
  }, [selectDietitian]);

  /**
   * Handle navigation to registration
   */
  const handleContinueToRegistration = useCallback(() => {
    if (workflowState.finalReferral?.referral_token) {
      // Navigate to registration with referral token
      navigate(`/patient/register`,);
    }
  }, [workflowState.finalReferral, navigate]);

  /**
   * Handle error boundary reset
   */
  const handleErrorReset = useCallback(() => {
    resetWorkflow();
    window.location.reload();
  }, [resetWorkflow]);

  /**
   * Handle referral completion
   */
  const handleReferralComplete = (referralToken: string) => {
    logger.log('Referral completed:', referralToken);
  };

  /**
   * Handle creating a new referral
   * Clears session storage and resets workflow to first step
   */
  const handleCreateNewReferral = useCallback(() => {
    try {
      // Clear session storage for referral data
      if (typeof window !== 'undefined' && window.sessionStorage) {
        // Clear referral-specific storage keys
        const keysToRemove = [
          'bitewise_referral_workflow',
          'bitewise_referral_token'
        ];

        keysToRemove.forEach(key => {
          window.sessionStorage.removeItem(key);
        });
      }

      // Reset the workflow to initial state
      resetWorkflow();

      logger.log('New referral created - storage cleared and workflow reset');
    } catch (error) {
      logger.error('Failed to create new referral:', error);
    }
  }, [resetWorkflow, logger]);

  /**
   * Get current step content for accessibility
   */
  const getCurrentStepContent = () => {
    if (workflowState.currentStep === 'selection' && matches?.length === 0) {
      return EMPTY_SELECTION_CONTENT;
    }
    return STEP_CONTENT[workflowState.currentStep] || STEP_CONTENT.form;
  };

  // The tab title follows the heading shown on screen (one source of truth, so it cannot
  // drift from the empty-state heading if handlers are memoised later).
  const stepContent = getCurrentStepContent();
  useEffect(() => {
    document.title = `${stepContent.title} - BiteWise`;
  }, [stepContent.title]);

  // Show loading while initializing
  if (isInitializing) {
    return <LoadingSpinner message="Initializing referral process..." />;
  }

  // Show loading during API calls
  if (isLoading && workflowState.currentStep === 'form') {
    return <LoadingSpinner message="Finding your perfect dietitian match..." />;
  }

  if (isLoading && workflowState.currentStep === 'selection') {
    return <LoadingSpinner message="Creating your referral..." />;
  }

  return (
    <SelfReferralErrorBoundary onReset={handleErrorReset}>
      <Layout className={cn('min-h-screen')}>
        {/* Page Header */}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto text-center">
            <TitleText>
              {stepContent.title}
            </TitleText>
            <BodyText
              variant="secondary"
              className="text-lg"
            >
              {stepContent.description}
            </BodyText>

            {/* Progress Indicator for Screen Readers */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              Currently on step: {workflowState.currentStep}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className={cn(
          "flex-1",
          isDark ? "bg-secondary-900" : "bg-white"
        )}>
          <div className="container mx-auto px-4 py-4 sm:py-6">
            {/* Self-Referral Wizard */}
            <SelfReferralWizard
              providerId={providerId || ''}
              workflowState={workflowState}
              matches={matches}
              isLoading={isLoading}
              error={error}
              sessionToken={sessionToken || undefined}
              onFormSubmit={handleFormSubmit}
              onDietitianSelect={handleDietitianSelect}
              onSessionExpire={handleSessionExpire}
              onContinueToRegistration={handleContinueToRegistration}
              onComplete={handleReferralComplete}
              onReset={resetWorkflow}
              onBackToForm={resetWorkflow}
              onCreateNewReferral={handleCreateNewReferral}
              className="max-w-4xl mx-auto"
            />

            {/* Additional Information */}
            {workflowState.currentStep !== 'completed' && (
              <div className="max-w-2xl mx-auto mt-4">
                <div className={cn(
                  "rounded-lg p-6",
                  isDark ? "bg-secondary-900" : "bg-secondary-50"
                )}>
                  <h2 className={cn(
                    "text-lg font-semibold mb-3",
                    isDark ? "text-secondary-100" : "text-secondary-900"
                  )}>
                    Why Choose BiteWise?
                  </h2>
                  <ul className={cn(
                    "space-y-2 text-sm",
                    isDark ? "text-secondary-400" : "text-secondary-600"
                  )}>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Get matched with qualified, verified dietitians
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Receive personalized nutrition guidance
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Start your health journey with expert support
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Secure, HIPAA-compliant platform
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Support Information - Show on completion step */}
            {workflowState.currentStep === 'completed' && (
              <div className="max-w-2xl mx-auto mt-12">
                <div className="text-center mb-8">
                  <p className={cn(
                    "text-sm mb-6",
                    isDark ? "text-secondary-400" : "text-secondary-600"
                  )}>
                    If you have any questions about your referral or need help getting started, please don't hesitate to contact our support team.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={() => logger.log('Contact support')}
                      className="sm:w-auto"
                    >
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </Layout>
    </SelfReferralErrorBoundary>
  );
};

export default SelfReferralLandingPage;